import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowUpDown,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  History,
  Layers,
  MapPin,
  Package,
  RefreshCcw,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { fetchAllPaginated } from '../utils/productCatalog';
import { normalizeBrandLabel } from '../utils/brandFilters';
import { buildLocalDateRangeTimestamp, getLocalDateKey } from '../utils/localDateTime';

const formatCantidad = (value) =>
  Number(value || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

// Ventana por defecto del historial. Arrancaba en "hoy", y como el kardex se
// consulta casi siempre para revisar una venta ya cerrada, el movimiento buscado
// quedaba fuera del rango inicial y parecía no existir.
const DEFAULT_RANGE_DAYS = 7;

const shiftDays = (date, days) => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
};

const TIPO_GROUPS = {
  ingreso: ['Ingreso', 'INGRESO'],
  egreso: ['Egreso', 'EGRESO', 'Venta (Componente de Combo)'],
  transferencia: ['Transferencia', 'TRANSFERENCIA'],
};

const classifyTipo = (raw) => {
  const value = String(raw || '').trim();
  const lower = value.toLowerCase();
  if (lower.includes('transfer')) return 'transferencia';
  if (lower.includes('ingreso')) return 'ingreso';
  if (lower.includes('egreso') || lower.includes('venta') || lower.includes('salida')) return 'egreso';
  return 'otro';
};

const TIPO_TONE = {
  ingreso: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  egreso: 'bg-rose-50 text-rose-700 border-rose-100',
  transferencia: 'bg-blue-50 text-blue-700 border-blue-100',
  otro: 'bg-slate-100 text-slate-600 border-slate-200',
};

const TIPO_LABEL = {
  ingreso: 'Ingreso',
  egreso: 'Egreso',
  transferencia: 'Transferencia',
  otro: 'Otro',
};

const InventarioMovimientos = ({ onNavigate }) => {
  const { profile } = useAuth();

  const today = useMemo(() => new Date(), []);
  const defaultDesde = useMemo(
    () => getLocalDateKey(shiftDays(today, -(DEFAULT_RANGE_DAYS - 1))),
    [today],
  );
  const defaultHasta = useMemo(() => getLocalDateKey(today), [today]);

  const [loading, setLoading] = useState(true);
  const [sitios, setSitios] = useState([]);
  const [productos, setProductos] = useState([]);
  const [movimientos, setMovimientos] = useState([]);

  const [desde, setDesde] = useState(defaultDesde);
  const [hasta, setHasta] = useState(defaultHasta);
  // El historial arranca sin filtrar por sucursal a propósito: los ajustes viejos
  // y las cargas por importación pueden no tener sitio, y filtrar por la sucursal
  // activa los ocultaba sin que el usuario supiera que había un filtro puesto.
  const [filtroSitio, setFiltroSitio] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroProductoId, setFiltroProductoId] = useState('');
  const [prodQuery, setProdQuery] = useState('');
  const [prodDropdownOpen, setProdDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  useEffect(() => {
    const handler = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) setProdDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (profile?.organization_id) fetchMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (profile?.organization_id) fetchMovimientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, desde, hasta]);

  const fetchMasters = async () => {
    try {
      const orgId = profile.organization_id;
      const [resSitios, resProds] = await Promise.all([
        supabase.from('sitios').select('id, nombre').eq('organization_id', orgId).order('nombre'),
        fetchAllPaginated(() =>
          supabase
            .from('products')
            .select('id, name, brand, barcode, unit_of_measure')
            .eq('organization_id', orgId)
            .order('name', { ascending: true })
        ),
      ]);
      setSitios(resSitios.data || []);
      setProductos(resProds || []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar maestros');
    }
  };

  const fetchMovimientos = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;
      // El rango se arma en hora local con offset explícito. Antes se armaba en
      // UTC ('...T00:00:00.000Z'): en Argentina (UTC-3) el "día" consultado iba
      // de las 21:00 del día anterior a las 21:00 del día elegido, así que las
      // operaciones de la noche caían en el día siguiente y las primeras tres
      // horas del turno desaparecían. Mismo bug que ya se corrigió en Caja.
      const desdeIso = buildLocalDateRangeTimestamp(desde, false);
      const hastaIso = buildLocalDateRangeTimestamp(hasta, true);

      const rows = await fetchAllPaginated(() => {
        let query = supabase
          .from('movimientos_stock')
          .select(
            'id, created_at, tipo, cantidad, motivo, producto_id, sitio_origen, sitio_destino, deposito_origen, deposito_destino, products(name, brand, barcode, unit_of_measure)'
          )
          .eq('organization_id', orgId);

        // Una fecha vacía o inválida devuelve null: en ese caso no se acota ese
        // extremo del rango en vez de mandarle null a PostgREST.
        if (desdeIso) query = query.gte('created_at', desdeIso);
        if (hastaIso) query = query.lte('created_at', hastaIso);

        return query.order('created_at', { ascending: false });
      });
      setMovimientos(rows || []);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const sitioMap = useMemo(() => new Map(sitios.map((s) => [s.id, s.nombre])), [sitios]);
  const productoMap = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);

  const productosFiltradosLista = useMemo(() => {
    const term = prodQuery.trim().toLowerCase();
    if (!term) return productos.slice(0, 40);
    return productos
      .filter((p) => {
        const name = (p.name || '').toLowerCase();
        const bc = (p.barcode || '').toLowerCase();
        const brand = normalizeBrandLabel(p.brand, '').toLowerCase();
        return name.includes(term) || bc.includes(term) || brand.includes(term);
      })
      .slice(0, 60);
  }, [productos, prodQuery]);

  const productoSeleccionado = filtroProductoId ? productoMap.get(filtroProductoId) : null;

  const movimientosFiltrados = useMemo(() => {
    return (movimientos || []).filter((m) => {
      if (filtroProductoId && m.producto_id !== filtroProductoId) return false;
      const cls = classifyTipo(m.tipo);
      if (filtroTipo !== 'todos' && cls !== filtroTipo) return false;
      if (filtroSitio !== 'todas') {
        if (m.sitio_origen !== filtroSitio && m.sitio_destino !== filtroSitio) return false;
      }
      return true;
    });
  }, [movimientos, filtroProductoId, filtroTipo, filtroSitio]);

  const movimientosOrdenados = useMemo(() => {
    const arr = [...movimientosFiltrados];
    const { key, direction } = sortConfig;
    const getValue = (m) => {
      switch (key) {
        case 'created_at':
          return new Date(m.created_at || 0).getTime();
        case 'producto':
          return m.products?.name || productoMap.get(m.producto_id)?.name || '';
        case 'tipo':
          return classifyTipo(m.tipo);
        case 'cantidad':
          return Number(m.cantidad || 0);
        case 'origen':
          return sitioMap.get(m.sitio_origen) || '';
        case 'destino':
          return sitioMap.get(m.sitio_destino) || '';
        default:
          return '';
      }
    };
    arr.sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (typeof av === 'string' || typeof bv === 'string') {
        const as = String(av ?? '').toLowerCase();
        const bs = String(bv ?? '').toLowerCase();
        return direction === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
      }
      const an = Number(av || 0);
      const bn = Number(bv || 0);
      return direction === 'asc' ? an - bn : bn - an;
    });
    return arr;
  }, [movimientosFiltrados, sortConfig, sitioMap, productoMap]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      const defaultDescKeys = ['created_at', 'cantidad'];
      return { key, direction: defaultDescKeys.includes(key) ? 'desc' : 'asc' };
    });
  };

  const SortableTh = ({ label, sortKey, align = 'left', className = '' }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <th
        className={`group px-4 py-4 cursor-pointer hover:bg-slate-100 transition-colors select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
          <div className="leading-tight text-inherit">{label}</div>
          <div className="flex flex-col">
            {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
            {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
            {!isActive && <div className="h-2.5 w-2.5 opacity-0 group-hover:opacity-30"><Filter size={10} /></div>}
          </div>
        </div>
      </th>
    );
  };

  const kpis = useMemo(() => {
    const result = { total: 0, ingreso: 0, egreso: 0, transferencia: 0, qIngreso: 0, qEgreso: 0 };
    movimientosFiltrados.forEach((m) => {
      const cls = classifyTipo(m.tipo);
      result.total += 1;
      const cant = Number(m.cantidad || 0);
      if (cls === 'ingreso') {
        result.ingreso += 1;
        result.qIngreso += cant;
      } else if (cls === 'egreso') {
        result.egreso += 1;
        result.qEgreso += cant;
      } else if (cls === 'transferencia') {
        result.transferencia += 1;
      }
    });
    return result;
  }, [movimientosFiltrados]);

  const handleExportar = async () => {
    if (!movimientosOrdenados.length) {
      toast.error('No hay movimientos para exportar');
      return;
    }
    let XLSX;
    try {
      const mod = await import('xlsx');
      XLSX = mod?.default || mod;
    } catch (error) {
      toast.error('No se pudo cargar el exportador de Excel');
      return;
    }

    const rows = movimientosOrdenados.map((m) => {
      const prod = m.products || productoMap.get(m.producto_id) || {};
      return {
        Fecha: m.created_at ? new Date(m.created_at).toLocaleString('es-AR') : '',
        Tipo: TIPO_LABEL[classifyTipo(m.tipo)] || m.tipo || '',
        Producto: prod.name || '',
        Marca: normalizeBrandLabel(prod.brand) || '',
        'Codigo de barra': prod.barcode || '',
        Cantidad: Number(m.cantidad || 0),
        Unidad: prod.unit_of_measure || 'UN',
        Origen: sitioMap.get(m.sitio_origen) || '',
        'Deposito origen': m.deposito_origen || '',
        Destino: sitioMap.get(m.sitio_destino) || '',
        'Deposito destino': m.deposito_destino || '',
        Motivo: m.motivo || '',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos');
    XLSX.writeFile(
      workbook,
      `${BRAND_NAME}_Movimientos_${desde}_${hasta}.xlsx`
    );
  };

  const limpiarFiltros = () => {
    setFiltroSitio('todas');
    setFiltroTipo('todos');
    setFiltroProductoId('');
    setProdQuery('');
    setDesde(defaultDesde);
    setHasta(defaultHasta);
  };

  const sucursalLabel = (id) => sitioMap.get(id) || '—';

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('inventario')}
            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
            title="Volver a Inventario"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <History size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Historial de movimientos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Ingresos, egresos y transferencias por sucursal
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={fetchMovimientos}
            className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <RefreshCcw size={16} /> Actualizar
          </button>
          <button
            onClick={handleExportar}
            className="w-full sm:w-auto px-5 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Download size={16} /> Exportar
          </button>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total movimientos</p>
            <BarChart3 size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En el rango</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm border-l-4 border-l-emerald-500">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ingresos</p>
            <ArrowUpDown size={16} className="text-emerald-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-emerald-600 tracking-tight">{kpis.ingreso}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{formatCantidad(kpis.qIngreso)} u.</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm border-l-4 border-l-rose-500">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Egresos</p>
            <ArrowUpDown size={16} className="text-rose-300 rotate-180" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-rose-500 tracking-tight">{kpis.egreso}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{formatCantidad(kpis.qEgreso)} u.</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Transferencias</p>
            <ArrowLeftRight size={16} className="text-blue-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-blue-500 tracking-tight">{kpis.transferencia}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Entre sucursales</p>
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2.5">
          {/* DESDE */}
          <div className="md:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Calendar size={12} /> Desde
            </label>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
            />
          </div>

          {/* HASTA */}
          <div className="md:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Calendar size={12} /> Hasta
            </label>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
            />
          </div>

          {/* PRODUCTO */}
          <div className="md:col-span-4 relative" ref={dropdownRef}>
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Package size={12} /> Producto
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                type="text"
                value={
                  productoSeleccionado
                    ? `${productoSeleccionado.name}${productoSeleccionado.barcode ? ` [${productoSeleccionado.barcode}]` : ''}`
                    : prodQuery
                }
                onChange={(event) => {
                  setFiltroProductoId('');
                  setProdQuery(event.target.value);
                  setProdDropdownOpen(true);
                }}
                onFocus={() => setProdDropdownOpen(true)}
                placeholder="TODOS LOS PRODUCTOS"
                className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold uppercase outline-none focus:border-[#51A7FD] shadow-inner"
              />
              {(productoSeleccionado || prodQuery) && (
                <button
                  type="button"
                  onClick={() => {
                    setFiltroProductoId('');
                    setProdQuery('');
                    setProdDropdownOpen(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  title="Limpiar"
                >
                  <X size={14} />
                </button>
              )}

              {prodDropdownOpen && productosFiltradosLista.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                  {productosFiltradosLista.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setFiltroProductoId(p.id);
                        setProdQuery('');
                        setProdDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-all border-b border-slate-50 last:border-0"
                    >
                      <div className="text-[11px] font-black uppercase tracking-tight text-slate-800 leading-none flex items-center justify-between gap-3">
                        <span className="truncate">{p.name}</span>
                        <span className="shrink-0 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-100 text-[8px] font-black uppercase tracking-widest">
                          {normalizeBrandLabel(p.brand)}
                        </span>
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-1">
                        {p.barcode || 'S/COD'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* SUCURSAL */}
          <div className="md:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <MapPin size={12} /> Sucursal
            </label>
            <select
              value={filtroSitio}
              onChange={(e) => setFiltroSitio(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="todas">TODAS (CONSOLIDADO)</option>
              {sitios.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>

          {/* TIPO */}
          <div className="md:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Layers size={12} /> Tipo
            </label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="todos">TODOS</option>
              <option value="ingreso">INGRESOS</option>
              <option value="egreso">EGRESOS</option>
              <option value="transferencia">TRANSFERENCIAS</option>
              <option value="otro">OTROS</option>
            </select>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={limpiarFiltros}
            className="px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border bg-slate-50 text-slate-500 border-slate-100 hover:text-slate-700"
          >
            <X size={12} className="inline mr-1" /> Limpiar filtros
          </button>
          {filtroSitio !== 'todas' && (
            <span className="px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border bg-blue-50 text-blue-700 border-blue-100">
              Sucursal: {sucursalLabel(filtroSitio)}
            </span>
          )}
          {productoSeleccionado && (
            <span className="px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border bg-blue-50 text-blue-700 border-blue-100 truncate max-w-[260px]">
              Producto: {productoSeleccionado.name}
            </span>
          )}
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-10">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Movimientos ({movimientosOrdenados.length})
            </span>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center text-slate-400">
            <RefreshCcw className="animate-spin mb-3 text-[#51A7FD]" size={28} />
            <p className="text-[10px] font-black uppercase tracking-widest">Cargando movimientos...</p>
          </div>
        ) : (
          <>
            {/* MOBILE */}
            <div className="lg:hidden divide-y divide-slate-100">
              {movimientosOrdenados.length === 0 ? (
                <div className="py-14 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                  Sin movimientos en el período
                </div>
              ) : (
                movimientosOrdenados.map((m) => {
                  const prod = m.products || productoMap.get(m.producto_id) || {};
                  const cls = classifyTipo(m.tipo);
                  return (
                    <div key={m.id} className="p-4 bg-white">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[11px] font-black text-slate-800 uppercase tracking-tight break-words">
                            {prod.name || '—'}
                          </p>
                          <p className="mt-1 text-[9px] font-bold text-slate-400 uppercase tracking-widest break-words">
                            {normalizeBrandLabel(prod.brand)} · {prod.barcode || 'S/COD'}
                          </p>
                        </div>
                        <span className={`shrink-0 inline-flex px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${TIPO_TONE[cls]}`}>
                          {TIPO_LABEL[cls]}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Fecha</p>
                          <p className="mt-1 text-[11px] font-black text-slate-700">
                            {m.created_at ? new Date(m.created_at).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-right">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Cantidad</p>
                          <p className="mt-1 text-[12px] font-black text-slate-700">
                            {formatCantidad(m.cantidad)} <span className="text-[9px] text-slate-400 font-bold">{prod.unit_of_measure || 'UN'}</span>
                          </p>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 col-span-2">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Sucursal</p>
                          <p className="mt-1 text-[10px] font-bold text-slate-700 uppercase">
                            {sucursalLabel(m.sitio_origen) !== '—' ? sucursalLabel(m.sitio_origen) : '—'}
                            {m.deposito_origen ? ` · ${m.deposito_origen}` : ''}
                            {cls === 'transferencia' && (
                              <>
                                {' → '}
                                {sucursalLabel(m.sitio_destino)}
                                {m.deposito_destino ? ` · ${m.deposito_destino}` : ''}
                              </>
                            )}
                            {cls !== 'transferencia' && m.sitio_destino && (
                              <>
                                {' '}
                                <span className="text-slate-400">→</span> {sucursalLabel(m.sitio_destino)}
                              </>
                            )}
                          </p>
                        </div>
                        {m.motivo && (
                          <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 col-span-2">
                            <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Motivo</p>
                            <p className="mt-1 text-[10px] font-bold text-slate-600 uppercase">{m.motivo}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* DESKTOP */}
            <div className="hidden lg:block">
              <table className="w-full table-fixed text-left">
                <colgroup>
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '23%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <SortableTh label="Fecha" sortKey="created_at" className="px-3" />
                    <SortableTh label="Tipo" sortKey="tipo" className="px-2" />
                    <SortableTh label="Producto" sortKey="producto" className="px-2" />
                    <SortableTh label="Cantidad" sortKey="cantidad" align="right" className="px-2" />
                    <SortableTh label="Sucursal" sortKey="origen" className="px-2" />
                    <th className="px-2 py-3">Motivo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {movimientosOrdenados.map((m) => {
                    const prod = m.products || productoMap.get(m.producto_id) || {};
                    const cls = classifyTipo(m.tipo);
                    const origenN = sucursalLabel(m.sitio_origen);
                    const destinoN = sucursalLabel(m.sitio_destino);
                    return (
                      <tr key={m.id} className="hover:bg-blue-50/20 transition-all">
                        <td className="px-3 py-2.5 align-top">
                          <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest whitespace-nowrap">
                            {m.created_at ? new Date(m.created_at).toLocaleDateString('es-AR') : '—'}
                          </span>
                          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                            {m.created_at ? new Date(m.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </div>
                        </td>
                        <td className="px-2 py-2.5 align-top">
                          <span className={`inline-flex px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wide border ${TIPO_TONE[cls]}`}>
                            {TIPO_LABEL[cls]}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 align-top">
                          <div className="flex flex-col min-w-0">
                            <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight truncate" title={prod.name || ''}>
                              {prod.name || '—'}
                            </span>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate">
                              {normalizeBrandLabel(prod.brand)}{prod.barcode ? ` · ${prod.barcode}` : ''}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-right align-top whitespace-nowrap">
                          <span className={`text-[10px] font-black uppercase tracking-wide ${cls === 'egreso' ? 'text-rose-600' : cls === 'ingreso' ? 'text-emerald-600' : 'text-slate-700'}`}>
                            {cls === 'egreso' ? '-' : cls === 'ingreso' ? '+' : ''}{formatCantidad(m.cantidad)} {prod.unit_of_measure || 'UN'}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 align-top">
                          <div className="flex flex-col min-w-0 text-[10px] font-black text-slate-700 uppercase tracking-wide">
                            {cls === 'transferencia' ? (
                              <span className="truncate" title={`${origenN} → ${destinoN}`}>
                                {origenN} <span className="text-slate-400">→</span> {destinoN}
                              </span>
                            ) : cls === 'egreso' ? (
                              <span className="truncate" title={origenN}>{origenN}</span>
                            ) : (
                              <span className="truncate" title={destinoN}>{destinoN}</span>
                            )}
                            {(m.deposito_origen || m.deposito_destino) && (
                              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate">
                                {[m.deposito_origen, m.deposito_destino].filter(Boolean).join(' → ')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2.5 align-top">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide line-clamp-2" title={m.motivo || ''}>
                            {m.motivo || '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {movimientosOrdenados.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-20 text-center opacity-30 italic text-[10px] font-black uppercase tracking-widest">
                        Sin movimientos en el período
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InventarioMovimientos;
